import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setStoredToken } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Email/password — customer + worker
  const loginWithCredentials = async (email, password, expected_role) => {
    const payload = { email, password };
    if (expected_role) payload.expected_role = expected_role;
    const res = await api.post('/auth/jwt/login', payload);
    // Persist Bearer-fallback token first so subsequent requests are auth'd
    // even when 3rd-party cookies are blocked (Safari ITP / Brave / strict mode).
    if (res.data?.access_token) setStoredToken(res.data.access_token);
    if (res.data) {
      // Strip server-only fields that we don't want in client state.
      const { access_token, ...userOnly } = res.data;
      setUser(userOnly);
    }
    setLoading(false);
    return res.data;
  };

  const registerCustomer = async ({ name, email, password, phone, referral_code }) => {
    const res = await api.post('/auth/jwt/register', {
      name, email, password, phone: phone || '', role: 'customer',
      referral_code: referral_code || '',
    });
    if (res.data?.access_token) setStoredToken(res.data.access_token);
    if (res.data) {
      const { access_token, ...userOnly } = res.data;
      setUser(userOnly);
    }
    setLoading(false);
    return res.data;
  };

  // Backward-compat: any old call to `login()` without args is now redirected
  // to a UX hint — pages should switch to /login form.
  const login = () => { window.location.href = '/login'; };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setStoredToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, loginWithCredentials, registerCustomer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
